import { useEffect, useState } from 'react';
import { timetableAPI } from '../services/api';
import { Logger } from '../utils/logger';

export interface OngoingClass {
  subjectName: string;
  /** "HH:MM" — when the current period ends. */
  endTime: string;
}

const parseTime = (timeStr: string) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
};

/**
 * The teaching slot in progress RIGHT NOW for a class, from the real timetable
 * (same source and rules as the header's "In class • …" badge). Null whenever
 * nothing is live: before school, after school, breaks, free periods, no
 * timetable. Re-evaluated every minute so the answer tracks the clock.
 */
export function useOngoingClass(classId: string | undefined): OngoingClass | null {
  const [ongoing, setOngoing] = useState<OngoingClass | null>(null);

  useEffect(() => {
    if (!classId) {
      setOngoing(null);
      return;
    }

    let isMounted = true;
    let slots: any[] | null = null;

    const evaluate = () => {
      if (!isMounted || !slots) return;
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      const slot = slots.find((s: any) =>
        s.isTeachingSlot !== false &&
        currentMinutes >= parseTime(s.startTime) &&
        currentMinutes <= parseTime(s.endTime));

      setOngoing(slot
        ? {
            subjectName: slot.subjectName || slot.slotName || 'Class',
            endTime: (slot.endTime || '').slice(0, 5),
          }
        : null);
    };

    (async () => {
      try {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');

        const res = await timetableAPI.getTimetable(classId, `${yyyy}-${mm}-${dd}`, 'weekly');
        const daysArray = res.data?.days ?? [];
        const todayData = daysArray.find((d: any) => d.dayOfWeek === today.getDay());
        slots = todayData?.slots ?? null;
        evaluate();
      } catch (err) {
        Logger.warn('OngoingClass', 'Failed to fetch timetable', err);
      }
    })();

    const timer = setInterval(evaluate, 60_000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [classId]);

  return ongoing;
}
